import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Countdown from "@/components/Countdown";
import { ProductGridSkeleton } from "@/components/Skeletons";
import { ArrowRight, Check } from "lucide-react";

const HERO_BG = "/hero-model.jpg";
const HERO_2 = "https://images.unsplash.com/photo-1768084356884-22bb77e76931";

export default function Home() {
  const [data, setData] = useState(null);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    api.get("/products/featured").then((r) => setData(r.data));
    api.get("/banners").then((r) => setBanners(r.data.banners || []));
  }, []);

  const dropDate = data?.drops?.[0]?.drop_release_at;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[640px] bg-white overflow-hidden grain">
        <div className="absolute inset-0 grid grid-cols-1 lg:grid-cols-12">
          {/* Image side */}
          <div className="hidden lg:block lg:col-span-7 lg:col-start-6 relative">
            <img src={HERO_BG} alt="BYONDELMNTS Drop 014 hoodie" className="absolute inset-0 w-full h-full object-cover object-[center_20%]" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/30 to-transparent" />
          </div>
          {/* Mobile image */}
          <img src={HERO_BG} alt="" className="lg:hidden absolute inset-0 w-full h-full object-cover object-[center_20%] opacity-40" />
        </div>

        <div className="relative max-w-[1600px] mx-auto h-full px-6 lg:px-10 flex flex-col justify-end pb-16">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#FF3B30] text-white px-3 py-1 font-display text-xs tracking-widest mb-6 animate-fade-up">
              DROP 014 — LIVE NOW
            </span>
            <h1 className="font-display text-[15vw] sm:text-[10vw] lg:text-[140px] leading-[0.85] tracking-tighter text-black animate-fade-up" style={{ animationDelay: "80ms" }}>
              STATIC<span className="text-[#FF3B30] inline-block mx-2 align-middle text-[0.55em] translate-y-[-0.08em]">×</span>ELMNTS
            </h1>
            <p className="mt-6 text-black/70 max-w-md text-base animate-fade-up" style={{ animationDelay: "160ms" }}>
              500 pieces. Garment-dyed heavyweight. Engineered for the streets — not the runway.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6 animate-fade-up" style={{ animationDelay: "240ms" }}>
              <Link to="/drops" className="inline-flex items-center gap-2 bg-black text-white px-8 py-4 font-display tracking-widest text-sm hover-invert border border-black" data-testid="hero-cta-drops">
                SHOP THE DROP <ArrowRight size={16} />
              </Link>
              <Link to="/shop/men" className="inline-flex items-center gap-2 font-display tracking-widest text-sm text-black/80 hover:text-black border-b border-black/40 hover:border-black pb-1" data-testid="hero-cta-men">
                EXPLORE MEN <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DROP COUNTDOWN */}
      {dropDate && (
        <section className="border-y border-black/10 py-12">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <div className="font-mono text-xs text-black/40 tracking-widest">NEXT DROP RELEASE</div>
              <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-2">DROP 015 IS LOADING</h2>
            </div>
            <Countdown targetIso={dropDate} />
          </div>
        </section>
      )}

      {/* NEW DROPS */}
      <section className="py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="font-mono text-xs text-[#FF3B30] tracking-widest">01 / DROPS</div>
              <h2 className="font-display text-5xl md:text-7xl tracking-tighter mt-2">NEW DROPS</h2>
            </div>
            <Link to="/drops" className="font-display text-sm tracking-widest border-b border-black pb-1 hover:text-[#FF3B30]">VIEW ALL</Link>
          </div>
          {data ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {data.drops.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          ) : <ProductGridSkeleton />}
        </div>
      </section>

      {/* LOOKBOOK BANNER */}
      <section className="relative h-[60vh] min-h-[420px] my-12">
        <img src={HERO_2} alt="lookbook" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative max-w-[1600px] mx-auto h-full px-6 lg:px-10 flex items-center">
          <div>
            <div className="font-mono text-xs text-black/70 tracking-widest">LOOKBOOK · SS26</div>
            <h2 className="font-display text-6xl md:text-8xl tracking-tighter mt-4 max-w-3xl leading-[0.9]">
              REFUSE TO<br /><span className="text-[#FF3B30]">BLEND IN.</span>
            </h2>
            <Link to="/shop/women" className="inline-flex items-center gap-2 mt-8 border border-black px-8 py-4 font-display tracking-widest text-sm hover-invert" data-testid="lookbook-cta">
              SHOP WOMEN <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SALE / BESTSELLERS */}
      <section className="py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="font-mono text-xs text-[#FF3B30] tracking-widest">02 / TRENDING</div>
              <h2 className="font-display text-5xl md:text-7xl tracking-tighter mt-2">NEW ARRIVALS</h2>
            </div>
            <Link to="/shop/all" className="font-display text-sm tracking-widest border-b border-black pb-1 hover:text-[#FF3B30]">SHOP ALL</Link>
          </div>
          {data ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {data.new_arrivals.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          ) : <ProductGridSkeleton />}
        </div>
      </section>

      {/* MEMBERSHIP TIERS */}
      <section className="border-y border-black/10 py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="font-mono text-xs text-[#FF3B30] tracking-widest">03 / ELMNTS CLUB</div>
            <h2 className="font-display text-5xl md:text-7xl tracking-tighter mt-2 leading-[0.9]">EARN. UNLOCK.<br />WEAR FIRST.</h2>
            <p className="text-black/60 mt-4 max-w-xl">Three tiers. Auto-upgraded by lifetime spend. Cancel anytime.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <TierCard
              name="FREE"
              price="₹0"
              tagline="START HERE"
              perks={[
                "1 point per ₹100 spent",
                "Standard shipping",
                "Wishlist + drop alerts",
                "Refer & earn ₹250",
              ]}
              cta="JOIN FREE"
              ctaTo="/register"
            />
            <TierCard
              name="PRO"
              price="₹15K+ SPEND"
              tagline="MOST POPULAR"
              highlight
              perks={[
                "2 points per ₹100 spent",
                "FREE shipping always",
                "Early drop access — 24 hrs",
                "Birthday bonus ₹500",
                "Refer & earn ₹500",
              ]}
              cta="UNLOCK PRO"
              ctaTo="/register"
            />
            <TierCard
              name="ELITE"
              price="₹50K+ SPEND"
              tagline="INVITE-ONLY DROPS"
              perks={[
                "3 points per ₹100 spent",
                "FREE priority shipping",
                "Early drop access — 72 hrs",
                "Members-only collabs",
                "Personal stylist on WhatsApp",
                "Refer & earn ₹1000",
              ]}
              cta="LEARN MORE"
              ctaTo="/register"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function TierCard({ name, price, tagline, perks, cta, ctaTo, highlight }) {
  return (
    <div className={`relative border ${highlight ? "border-[#FF3B30]" : "border-black/15"} p-8 flex flex-col`} data-testid={`tier-${name.toLowerCase()}`}>
      {highlight && (
        <span className="absolute -top-3 left-6 bg-[#FF3B30] text-white px-3 py-1 font-display tracking-widest text-[10px]">{tagline}</span>
      )}
      {!highlight && (
        <span className="font-mono text-[10px] tracking-widest text-black/40">{tagline}</span>
      )}
      <h3 className={`font-display tracking-tighter mt-2 ${highlight ? "text-6xl text-[#FF3B30]" : "text-5xl"}`}>{name}</h3>
      <div className="font-mono text-xs text-black/50 mt-2">{price}</div>
      <ul className="space-y-3 mt-6 flex-1">
        {perks.map((perk) => (
          <li key={perk} className="flex items-start gap-2 text-sm">
            <Check size={14} className={`mt-0.5 flex-shrink-0 ${highlight ? "text-[#FF3B30]" : "text-black/60"}`} />
            <span className="text-black/80">{perk}</span>
          </li>
        ))}
      </ul>
      <Link
        to={ctaTo}
        className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 font-display tracking-widest text-xs ${
          highlight ? "bg-[#FF3B30] text-white hover:bg-black" : "border border-black text-black hover-invert"
        }`}
      >
        {cta} <ArrowRight size={14} />
      </Link>
    </div>
  );
}
